import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const GIS_SRC = "https://accounts.google.com/gsi/client";

// Singleton loader promise — multiple components mounting the script tag once.
let gisPromise: Promise<void> | null = null;
function loadGsiScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("ssr"));
  if (gisPromise) return gisPromise;
  gisPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    if (existing) {
      if ((existing as HTMLScriptElement).dataset.loaded === "true") return resolve();
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("gis load failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = GIS_SRC;
    s.async = true;
    s.defer = true;
    s.dataset.loaded = "false";
    s.addEventListener("load", () => {
      s.dataset.loaded = "true";
      resolve();
    });
    s.addEventListener("error", () => reject(new Error("gis load failed")));
    document.head.appendChild(s);
  });
  return gisPromise;
}

type Props = {
  onCredential: (idToken: string) => void | Promise<void>;
  /** When true, button renders disabled with a tooltip. Use while a request is in flight. */
  disabled?: boolean;
  /** "signin_with" (default) or "signup_with" — controls the button label */
  text?: "signin_with" | "signup_with" | "continue_with";
};

/**
 * Renders Google's official sign-in button using Google Identity Services.
 * The Client ID comes from VITE_GOOGLE_CLIENT_ID; if missing, the button is
 * hidden entirely (graceful degradation in dev).
 */
export default function GoogleSignInButton({ onCredential, disabled, text = "continue_with" }: Props) {
  const { t } = useTranslation("auth");
  const ref = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    loadGsiScript()
      .then(() => {
        if (cancelled) return;
        // @ts-expect-error — google global injected by GIS script
        const g = window.google?.accounts?.id;
        if (!g || !ref.current) return;
        g.initialize({
          client_id: clientId,
          callback: (resp: { credential?: string }) => {
            if (resp.credential) void onCredential(resp.credential);
          },
          ux_mode: "popup",
          auto_select: false,
        });
        g.renderButton(ref.current, {
          theme: "outline",
          size: "large",
          text,
          shape: "pill",
          logo_alignment: "left",
          width: ref.current.offsetWidth || 320,
        });
        setReady(true);
      })
      .catch((e) => setError(e.message ?? t("auth.googleLoadError")));
    return () => {
      cancelled = true;
    };
  }, [clientId, onCredential, text]);

  if (!clientId) {
    // Dev-time fallback — keeps the layout slot but explains why nothing rendered.
    return (
      <div className="text-xs text-gray-400 text-center py-2">
        {t("auth.googleNotConfigured")}
      </div>
    );
  }

  return (
    <div className={disabled ? "opacity-50 pointer-events-none" : ""}>
      <div ref={ref} className="w-full flex justify-center [&>div]:w-full" />
      {!ready && !error && (
        <div className="text-xs text-gray-400 text-center py-2">{t("auth.loadingGoogle")}</div>
      )}
      {error && <div className="text-xs text-red-500 text-center py-2">{error}</div>}
    </div>
  );
}
